import { useMemo } from "react";
import type { DateRange } from "@/hooks/useDateRange";
import { getPreviousAnalyticsDateRange } from "../utils/analyticsDateUtils";
import { EMPTY_DATE_RANGE } from "./useOverviewAnalytics";
import {
  useOfferConversionOverview,
  useOrdersByDayOverview,
  useRatingsByStayOverview,
} from "./useOverviewAnalytics";
import { useRevenueTotal } from "./useRevenueTotal";

/**
 * Fetches overview + revenue metrics for the period immediately before
 * the selected date range (same length). Used only for KPI delta labels.
 */
export function useOverviewKpiPeriodComparison(dateRange: DateRange) {
  const previousRange = useMemo(
    () => getPreviousAnalyticsDateRange(dateRange),
    [dateRange],
  );

  const comparisonRange = previousRange ?? EMPTY_DATE_RANGE;

  const offerConversion = useOfferConversionOverview(comparisonRange);
  const ratingsByStay = useRatingsByStayOverview(comparisonRange);
  const ordersByDay = useOrdersByDayOverview(comparisonRange);
  const revenueTotal = useRevenueTotal(comparisonRange);

  return {
    previousRange,
    offerConversion,
    ratingsByStay,
    ordersByDay,
    revenueTotal,
    isLoading:
      offerConversion.isLoading ||
      ratingsByStay.isLoading ||
      ordersByDay.isLoading ||
      revenueTotal.isLoading,
    isFetching:
      offerConversion.isFetching ||
      ratingsByStay.isFetching ||
      ordersByDay.isFetching ||
      revenueTotal.isFetching,
    isError:
      offerConversion.isError ||
      ratingsByStay.isError ||
      ordersByDay.isError ||
      revenueTotal.isError,
    error:
      offerConversion.error ??
      ratingsByStay.error ??
      ordersByDay.error ??
      revenueTotal.error,
  };
}
